import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatCurrency, formatDateBR } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Download, FileBarChart2 } from "lucide-react";
import { toast } from "sonner";

function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }, 100);
}

export default function Reports() {
    const [trips, setTrips] = useState([]);
    const [requests, setRequests] = useState([]);
    const [mes, setMes] = useState("");
    const [tipo, setTipo] = useState("todos");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (mes) params.mes = mes;
        if (tipo !== "todos") params.tipo = tipo;
        const [tRes, rRes] = await Promise.all([
            api.get("/trips", { params }),
            api.get("/requests"),
        ]);
        setTrips(tRes.data);
        // filtro cliente para requests por mês (usa data_viagem)
        const reqs = rRes.data || [];
        setRequests(mes ? reqs.filter((r) => (r.data_viagem || "").startsWith(mes)) : reqs);
        setLoading(false);
    }, [mes, tipo]);

    useEffect(() => { load(); }, [load]);

    const stats = useMemo(() => {
        const total = trips.reduce((acc, t) => acc + (t.valor || 0), 0);
        const fixo = trips.filter((t) => t.tipo === "fixo");
        const diaria = trips.filter((t) => t.tipo === "diaria");
        const porDestino = {};
        const porMotorista = {};
        trips.forEach((t) => {
            porDestino[t.destino] = (porDestino[t.destino] || 0) + (t.valor || 0);
            porMotorista[t.responsavel_nome] = (porMotorista[t.responsavel_nome] || 0) + (t.valor || 0);
        });
        const destinoTop = Object.entries(porDestino).sort((a, b) => b[1] - a[1]).slice(0, 10);
        const motoristaTop = Object.entries(porMotorista).sort((a, b) => b[1] - a[1]).slice(0, 10);
        return {
            total,
            fixoTotal: fixo.reduce((a, t) => a + (t.valor || 0), 0),
            diariaTotal: diaria.reduce((a, t) => a + (t.valor || 0), 0),
            fixoCount: fixo.length,
            diariaCount: diaria.length,
            destinoTop,
            motoristaTop,
        };
    }, [trips]);

    const exportCsv = async () => {
        try {
            const params = {};
            if (tipo !== "todos") params.tipo = tipo;
            if (mes) params.mes = mes;
            const res = await api.get("/trips/export", { params, responseType: "blob" });
            const filename = `viagens_${tipo || "todas"}_${mes || "completo"}.csv`;
            downloadBlob(res.data, filename);
        } catch {
            toast.error("Erro ao exportar");
        }
    };

    const exportRequestsCsv = async () => {
        try {
            const params = {};
            if (mes) params.mes = mes;
            const res = await api.get("/requests/export", { params, responseType: "blob" });
            const filename = `solicitacoes_${mes || "completo"}.csv`;
            downloadBlob(res.data, filename);
        } catch {
            toast.error("Erro ao exportar");
        }
    };

    const reqStatus = useMemo(() => {
        const s = { pendente: 0, aprovada: 0, negada: 0, concluida: 0 };
        requests.forEach((r) => { s[r.status] = (s[r.status] || 0) + 1; });
        return s;
    }, [requests]);

    return (
        <div className="space-y-6" data-testid="reports-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-3xl font-extrabold text-foreground flex items-center gap-3">
                        <FileBarChart2 className="w-7 h-7 text-primary" /> Relatórios
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">Resumo financeiro e operacional</p>
                </div>
                <Button onClick={exportCsv} data-testid="btn-export-report" className="gap-2">
                    <Download className="w-4 h-4" /> Exportar Viagens (CSV)
                </Button>
                <Button onClick={exportRequestsCsv} variant="outline" data-testid="btn-export-requests" className="gap-2">
                    <Download className="w-4 h-4" /> Exportar Solicitações (CSV)
                </Button>
            </div>

            <Card>
                <CardContent className="p-5 flex gap-3 flex-wrap items-end">
                    <div>
                        <label className="text-xs font-medium text-muted-foreground">Mês</label>
                        <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="w-48" data-testid="input-report-month" />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-muted-foreground">Tipo</label>
                        <Select value={tipo} onValueChange={setTipo}>
                            <SelectTrigger className="w-48" data-testid="select-report-tipo"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="todos">Todos</SelectItem>
                                <SelectItem value="fixo">Contratos</SelectItem>
                                <SelectItem value="diaria">Diária</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {(mes || tipo !== "todos") && (
                        <Button variant="ghost" size="sm" onClick={() => { setMes(""); setTipo("todos"); }}>Limpar filtros</Button>
                    )}
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card data-testid="report-total">
                    <CardContent className="p-6">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Total Geral</div>
                        <div className="mt-2 text-3xl font-extrabold text-primary">{formatCurrency(stats.total)}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{trips.length} viagens</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Contratos</div>
                        <div className="mt-2 text-3xl font-extrabold">{formatCurrency(stats.fixoTotal)}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{stats.fixoCount} viagens</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Diária</div>
                        <div className="mt-2 text-3xl font-extrabold">{formatCurrency(stats.diariaTotal)}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{stats.diariaCount} viagens</div>
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                    <CardHeader><CardTitle className="text-base">Top destinos</CardTitle></CardHeader>
                    <CardContent>
                        {stats.destinoTop.length === 0 ? (
                            <div className="text-sm text-muted-foreground">Sem dados</div>
                        ) : (
                            <div className="space-y-2">
                                {stats.destinoTop.map(([nome, valor]) => (
                                    <div key={nome} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                                        <span className="text-sm font-medium">{nome}</span>
                                        <span className="text-sm font-bold text-primary">{formatCurrency(valor)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle className="text-base">Top motoristas</CardTitle></CardHeader>
                    <CardContent>
                        {stats.motoristaTop.length === 0 ? (
                            <div className="text-sm text-muted-foreground">Sem dados</div>
                        ) : (
                            <div className="space-y-2">
                                {stats.motoristaTop.map(([nome, valor]) => (
                                    <div key={nome} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                                        <span className="text-sm font-medium">{nome}</span>
                                        <span className="text-sm font-bold text-primary">{formatCurrency(valor)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader><CardTitle className="text-base">Solicitações de transporte {mes ? `— ${mes}` : "(todas)"}</CardTitle></CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="rounded-lg p-3 bg-orange-50 border border-orange-200 text-center" data-testid="stat-req-pendente">
                            <div className="text-2xl font-extrabold text-orange-800">{reqStatus.pendente || 0}</div>
                            <div className="text-[10px] uppercase tracking-wide font-semibold text-orange-700">Pendentes</div>
                        </div>
                        <div className="rounded-lg p-3 bg-emerald-50 border border-emerald-200 text-center" data-testid="stat-req-aprovada">
                            <div className="text-2xl font-extrabold text-emerald-800">{reqStatus.aprovada || 0}</div>
                            <div className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700">Aprovadas</div>
                        </div>
                        <div className="rounded-lg p-3 bg-blue-50 border border-blue-200 text-center" data-testid="stat-req-concluida">
                            <div className="text-2xl font-extrabold text-blue-800">{reqStatus.concluida || 0}</div>
                            <div className="text-[10px] uppercase tracking-wide font-semibold text-blue-700">Concluídas</div>
                        </div>
                        <div className="rounded-lg p-3 bg-red-50 border border-red-200 text-center" data-testid="stat-req-negada">
                            <div className="text-2xl font-extrabold text-red-800">{reqStatus.negada || 0}</div>
                            <div className="text-[10px] uppercase tracking-wide font-semibold text-red-700">Negadas</div>
                        </div>
                    </div>
                    {requests.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-4">Nenhuma solicitação no período</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-muted-foreground border-b border-border">
                                        <th className="py-2 px-2 font-semibold">Data</th>
                                        <th className="py-2 px-2 font-semibold">Solicitante</th>
                                        <th className="py-2 px-2 font-semibold">Destino</th>
                                        <th className="py-2 px-2 font-semibold">Status</th>
                                        <th className="py-2 px-2 font-semibold text-right">Valor estimado</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="reports-requests-body">
                                    {requests.slice(0, 50).map((r) => (
                                        <tr key={r.id} className="border-b border-border row-hover">
                                            <td className="py-2 px-2">{formatDateBR(r.data_viagem)}</td>
                                            <td className="py-2 px-2 font-medium">{r.solicitante_nome}</td>
                                            <td className="py-2 px-2">{r.destino}</td>
                                            <td className="py-2 px-2">
                                                <Badge variant="outline" className="uppercase text-[10px]">{r.status}</Badge>
                                            </td>
                                            <td className="py-2 px-2 text-right font-semibold">{r.valor_estimado > 0 ? formatCurrency(r.valor_estimado) : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {requests.length > 50 && (
                                <div className="text-xs text-muted-foreground mt-2 text-center">Mostrando 50 de {requests.length}. Baixe o CSV para ver tudo.</div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader><CardTitle className="text-base">Detalhamento das viagens</CardTitle></CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="text-muted-foreground text-center py-4">Carregando...</div>
                    ) : trips.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-8">Nenhuma viagem no período</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-muted-foreground border-b border-border">
                                        <th className="py-2 px-2 font-semibold">Data</th>
                                        <th className="py-2 px-2 font-semibold">Tipo</th>
                                        <th className="py-2 px-2 font-semibold">Responsável</th>
                                        <th className="py-2 px-2 font-semibold">Destino</th>
                                        <th className="py-2 px-2 font-semibold">Placa</th>
                                        <th className="py-2 px-2 font-semibold text-right">Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {trips.map((t) => (
                                        <tr key={t.id} className="border-b border-border row-hover">
                                            <td className="py-2 px-2">{formatDateBR(t.data)}</td>
                                            <td className="py-2 px-2">
                                                <Badge variant={t.tipo === "fixo" ? "default" : "secondary"} className="uppercase text-[10px]">{t.tipo}</Badge>
                                            </td>
                                            <td className="py-2 px-2">{t.responsavel_nome}</td>
                                            <td className="py-2 px-2">{t.destino}</td>
                                            <td className="py-2 px-2 font-mono">{t.veiculo_placa}</td>
                                            <td className="py-2 px-2 text-right font-semibold">{formatCurrency(t.valor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
